// @ts-check
import React, { useContext, useEffect, useLayoutEffect, useMemo } from 'react';
import { formatNumber, formatPercent } from '/b2b/common/helpers/Number';

import { CMP_CONSENT_CATEGORIES } from '/b2b/consent/constants';
import ConsentDashboardContext from '../ConsentDashboard.context';
import ErrorBoundary from '/b2b/common/components/ErrorBoundary';
import { GeoEChart } from '/b2b/common/components/Charts';
import PropTypes from 'prop-types';
import WidgetContainer from '/b2b/common/components/Reporting/WidgetContainer';
import { escapeCSVValue } from '/b2b/common/helpers/CSV';
import { fetchConsentReportsDashboard } from '/b2b/consent/services';
import { getLocaleCode } from '/b2b/common/state/locale/Locale.selectors';
import { getMapData } from '/b2b/common/components/Charts/EChart.geo';
import { makeDownloadBlob } from '/b2b/common/components/Reporting/helpers';
import merge from 'lodash/merge';
import { translate } from '/b2b/common/helpers/i18n';
import useBoundCallback from '/b2b/common/helpers/Hooks/useBoundCallback';
import { useSelector } from 'react-redux';
import { withAsyncData } from '/b2b/common/hoc/withAsyncData';
import withRetranslate from '/b2b/common/hoc/withRetranslate';

export const REPORT_NAME = 'consentsByJurisdiction';

/**
 * @typedef {Record<string,Partial<Record<'totalConsents'|CmpConsentCategories,number>>>} GeoLookup
 */

const csvColumnMap = {
    name: 'common.fields.name.label',
    jurisdiction: 'common.jurisdiction',
    totalConsents: 'common.total',
    .../** @type {{[K in CmpConsentCategoriesValues as `${K}TotalConsents`]: string;}} */ (
        Object.values(CMP_CONSENT_CATEGORIES).reduce((acc, category) => {
            switch (category) {
                case CMP_CONSENT_CATEGORIES.STORAGE:
                    return acc;
                default: {
                    acc[`${category}TotalConsents`] = [
                        'common.total',
                        `consent.categories.${category}`,
                    ];
                    break;
                }
            }
            return acc;
        }, {})
    ),
};

/**
 * @param {Unpacked<CmpReportsData['consentsByJurisdiction']>} row
 * @param {Array<keyof typeof csvColumnMap>} columnNames
 * @return
 */
const processRow = (row, columnNames) => {
    const { jurisdiction = '-' } = row || {};
    const [countryCode, divisionCode] = jurisdiction.split('-');
    const mapData = getMapData(`${jurisdiction}`);
    const { divisionLevel } = mapData;
    let divisionName = '';
    switch (divisionLevel) {
        case -1: {
            // Invalid jurisdiction, try to extract any data we can
            divisionName = translate(`countries.${countryCode}`, {
                _: translate('common.unknown'),
            });
            if (divisionCode) {
                divisionName += ` - ${translate(`divisions.${countryCode}-${divisionCode}`, {
                    _: translate('common.unknown'),
                })}`;
            }
            break;
        }
        case 2: {
            // Counties/Regions/Districts
            divisionName = `, ${translate(`regions.${jurisdiction}`, { _: translate('common.unknown') })}`;
        }
        // eslint-disable-next-line no-fallthrough
        case 1: {
            // States/Provinces/Territories
            divisionName = ` - ${translate(`divisions.${jurisdiction}`, {
                _: `${divisionCode.toLocaleUpperCase() || translate('common.unknown')}`,
            })}${divisionName}`;
        }
        // eslint-disable-next-line no-fallthrough
        case 0: {
            // Countries
            divisionName = `${translate(`countries.${countryCode}`, { _: translate('common.unknown') })}${divisionName}`;
            break;
        }

        default: {
            break;
        }
    }
    return columnNames
        .reduce((acc, column) => {
            switch (column) {
                case 'name':
                    acc.push(divisionName);
                    break;
                default:
                    acc.push(row?.[column]);
                    break;
            }
            return acc;
        }, /** @type {(undefined|string)[]} */ ([]))
        .filter(Boolean);
};

/**
 * @param {CmpReportsData} data
 * @returns {Blob}
 */
function makeCSV(data) {
    const { consentsByJurisdiction } = data || {};
    const columnNames = /** @type {Array<keyof typeof csvColumnMap>} */ (Object.keys(csvColumnMap));
    // String CSV
    const csvFile = Object.values(consentsByJurisdiction || {})
        .reduce(
            (/** @type {string[]} */ acc, row) => {
                const rowArray = processRow(row, columnNames).map(escapeCSVValue).join(',');
                return acc.concat(rowArray);
            },
            [
                columnNames
                    .map(column => translate(csvColumnMap[column], { delimiter: ' - ' }))
                    .map(escapeCSVValue)
                    .join(','),
            ]
        )
        .join('\n');
    return new Blob([csvFile], { type: 'text/csv;charset=utf-8;' });
}

/** @type {import('echarts').EChartsOption} */
const staticOptions = {
    color: ['#fff'],
    legend: {
        show: false,
    },
    tooltip: {},
    // @ts-ignore
    geo: {
        animation: true,
        left: 0,
        right: 0,
        emphasis: {
            disabled: true,
        },
        selectedMode: 'single',
        select: {
            itemStyle: {
                borderWidth: 0,
            },
        },
    },
    dataset: {
        sourceHeader: true,
    },
    visualMap: {
        type: 'continuous',
        inRange: {
            color: ['#7A3FF1'],
            colorAlpha: [0.5, 1],
            // colorHue: [260],
            // colorSaturation: [0.86],
            // colorLightness: [0.5, 0.8],
        },
        show: false,
    },
};

const ConsentsByJurisdiction = props => {
    const { filters, data: providedData, loading: providedLoading, shouldRetranslate } = props;
    const { data, error, load, loading } = useContext(ConsentDashboardContext);
    const localeCode = useSelector(getLocaleCode);
    const { consentRange = [0, 0], consentsByJurisdiction = [] } = data || {};
    const noResults = consentsByJurisdiction.length === 0;
    const isLoading = loading || providedLoading;

    /** @type {React.MutableRefObject<import("echarts/core").EChartsType|null>} */
    const echartsInstance = React.useRef(null);
    const helpers = useMemo(
        () => ({
            makeCSV,
            name: 'consents-by-jurisdiction',
        }),
        [makeCSV]
    );
    const makeDownload = useBoundCallback(makeDownloadBlob, [echartsInstance, data, helpers]);

    const [geoLookup, categories, center, zoom] = useMemo(() => {
        if (!consentsByJurisdiction?.length) {
            return [{}, [], [0, 0], 1];
        }

        let [center, zoom, total] = [[0, 0], 1, 0];
        const [geoLookup, categoriesSet] = consentsByJurisdiction.reduce(
            ([geoLookup, categoriesSet], row) => {
                const { jurisdiction, regionCode, consents, totalConsents } = row;
                const categories = /** @type {CmpConsentCategories[]} */ (Object.keys(consents));
                geoLookup[jurisdiction] = {
                    ...Object.entries(consents).reduce(
                        (acc, /** @type {[CmpConsentCategories,number]} */ [category, optIn]) => {
                            acc[category] = optIn;
                            return acc;
                        },
                        {}
                    ),
                    totalConsents,
                };

                if (
                    totalConsents > total &&
                    !regionCode // Only consider countries for the zoom, not regions
                ) {
                    total = totalConsents;
                    const mapData = getMapData(jurisdiction);
                    center = mapData.center;
                    zoom = mapData.zoom;
                }

                categories.forEach(category => categoriesSet.add(category));

                return [geoLookup, categoriesSet];
            },
            [/** @type {GeoLookup} */ ({}), /** @type {Set<CmpConsentCategories>} */ (new Set())]
        );

        return [geoLookup, Array.from(categoriesSet), center, zoom];
    }, [consentsByJurisdiction]);

    const renderTooltip = useBoundCallback(
        /** @type {(geoLookup: GeoLookup, categories: CmpConsentCategories[], localeCode: string, tooltipParams: import('echarts').TooltipComponentOption) => string} */
        (geoLookup, categories, localeCode, { name }) => {
            const mapData = getMapData(`${name}`);
            const { divisionCode, divisionLevel } = mapData;
            let divisionName = name;
            switch (divisionLevel) {
                case 0: {
                    // Countries
                    divisionName = translate(`countries.${divisionCode}`);
                    break;
                }
                case 1: {
                    // States/Provinces/Territories
                    divisionName = translate(`divisions.${divisionCode}`);
                    break;
                }
                default: {
                    divisionName = name;
                    break;
                }
            }

            const geoData = geoLookup[divisionCode];
            if (!categories.length || !geoData) {
                return '';
            }

            const validCategories = categories.filter(
                category => !['STORAGE'].includes(category) && geoData[category]
            );
            const total = geoData.totalConsents || 0;

            return `<b>${divisionName}</b><hr style="margin:0.25em 0"/>${validCategories
                .sort((a, b) => translate(a).localeCompare(translate(b)))
                .map(category => {
                    const value = geoLookup[divisionCode][category] || 0;
                    const percent = total > 0 ? (value / total) * 100 : 0;
                    return `${translate(`consent.categories.${CMP_CONSENT_CATEGORIES[category]}`)}: <b>${formatPercent(percent, localeCode, 2)}</b> (${formatNumber(value, localeCode)})`;
                })
                .join('<br>')}`;
        },
        [geoLookup, categories, localeCode]
    );

    const { countryData, regionalData } = useMemo(() => {
        const countryData = [];
        const regionalData = [];
        consentsByJurisdiction.forEach(row => {
            if (!row.regionCode) {
                countryData.push({ name: row.jurisdiction, value: row.totalConsents });
            } else {
                const { center } = getMapData(row.jurisdiction);
                regionalData.push({
                    name: row.jurisdiction,
                    value: [center[0], center[1], row.totalConsents],
                });
            }
        });
        return { countryData, regionalData };
    }, [consentsByJurisdiction]);

    const updateZoom = useBoundCallback(() => {
        if (echartsInstance.current) {
            echartsInstance.current.setOption({
                geo: {
                    center,
                    zoom,
                },
            });
        }
    }, [center, zoom]);
    useEffect(() => {
        updateZoom();
    }, [center, zoom]);
    const onChartReady = React.useCallback(chart => {
        echartsInstance.current = chart;
        updateZoom();
    }, []);

    const memoOption = useMemo(() => {
        return merge(
            {},
            staticOptions,
            /** @type {import('echarts').EChartsOption} */ ({
                geo: {
                    tooltip: {
                        formatter: renderTooltip,
                    },
                },
                visualMap: {
                    min: consentRange[0],
                    max: consentRange[1],
                },
                series: [
                    {
                        type: 'map',
                        geoIndex: 0,
                        emphasis: {
                            disabled: true,
                        },
                        animationDuration: 0,
                        data: countryData,
                    },
                    {
                        type: 'effectScatter',
                        colorBy: 'data',
                        coordinateSystem: 'geo',
                        geoIndex: 0,
                        animationDurationUpdate: 400, // This is the default duration for geo animations defined in Geo.echart.jsx
                        symbolSize: 8,
                        encode: {
                            tooltip: 2,
                        },
                        data: regionalData,
                    },
                ],
            })
        );
    }, [center, zoom, consentRange, countryData, regionalData, categories, shouldRetranslate]);

    /** @type {() => Promise<CmpReportsResponseData>} */
    const fetchData = useBoundCallback(
        filters =>
            fetchConsentReportsDashboard({
                filters: {
                    ...filters,
                    requestedCharts: [REPORT_NAME],
                },
            }),
        [filters]
    );
    useLayoutEffect(() => {
        providedData ? load(Promise.resolve(providedData)) : load(fetchData());
    }, [providedData]);

    return (
        <WidgetContainer
            disabled={noResults}
            downloadFormats={['csv', 'png']}
            echartsInstance={echartsInstance}
            error={error}
            title="consent.dashboard.charts.title.consentsByJurisdiction"
            tooltip="consent.dashboard.charts.tooltip.consentsByJurisdiction"
        >
            <ErrorBoundary>
                <GeoEChart
                    loading={isLoading}
                    makeDownload={makeDownload}
                    noResults={noResults}
                    onChartReady={onChartReady}
                    option={memoOption}
                    replaceMerge={['series']}
                />
            </ErrorBoundary>
        </WidgetContainer>
    );
};

ConsentsByJurisdiction.propTypes = {
    data: PropTypes.object,
    filters: PropTypes.object,
    loading: PropTypes.bool,
    shouldRetranslate: PropTypes.string,
};

/** @type {import('/b2b/common/hoc/withAsyncData').ComponentWithOverriddenProps<CmpReportsData, { filters: CmpReportsQueryParams }>} */
const TranslatedComponent = withRetranslate(ConsentsByJurisdiction);

export default withAsyncData(TranslatedComponent, ConsentDashboardContext);
